// UI设计规范标签宽度计算工具
import { isForeignLanguageEnv } from './languageUtils';

const widthCache = new Map<string, string>();
const columnWidthCache = new Map<string, string>();

// 清除所有缓存的辅助函数（用于测试和调试）
export const clearAllCaches = (): void => {
  widthCache.clear();
  columnWidthCache.clear();
};

/**
 * Tailwind safelist:
 * 由于本工具会在运行时拼接 `!w-[${width}]`，Tailwind 无法静态扫描到这些 class，
 * 会导致某些宽度（例如 72px、128px）在产物中没有对应 CSS，从而出现“看似未对齐/被内容撑开”的问题。
 *
 * 这里显式声明所有可能用到的宽度 class，确保 Tailwind 生成对应样式。
 */
const TAILWIND_WIDTH_CLASS_SAFELIST = [
  '!w-[36px]',
  '!w-[48px]',
  '!w-[60px]',
  '!w-[72px]',
  '!w-[84px]',
  '!w-[96px]',
  '!w-[104px]',
  '!w-[118px]',
  '!w-[130px]',
  '!w-[142px]'
];

const TAIAL_CHAR_COUNT_SAFELIST = [
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9
];

const extractWidthPxFromTailwindClass = (className: string): number => {
  const match = className.match(/\[([0-9]+)px\]/);
  if (!match) {
    return 0;
  }
  return Number.parseInt(match[1], 10);
};

const WIDTH_PX_SAFELIST = TAILWIND_WIDTH_CLASS_SAFELIST
  .map(extractWidthPxFromTailwindClass)
  .filter((n) => Number.isFinite(n) && n > 0);

const getSafelistWidthByCharCount = (totalChars: number): string => {
  if (WIDTH_PX_SAFELIST.length === 0) {
    return '36px';
  }

  // TAIAL_CHAR_COUNT_SAFELIST 和 TAILWIND_WIDTH_CLASS_SAFELIST 期望按索引一一对应（2字->36px, 3字->48px, ...）
  // 若 totalChars 超出枚举范围，则钳制到最后一个宽度
  const minCount = TAIAL_CHAR_COUNT_SAFELIST[0] ?? 2;
  const maxCount = TAIAL_CHAR_COUNT_SAFELIST[TAIAL_CHAR_COUNT_SAFELIST.length - 1] ?? minCount;
  const clampedChars = Math.min(Math.max(totalChars, minCount), maxCount);

  const idx = TAIAL_CHAR_COUNT_SAFELIST.indexOf(clampedChars);
  const widthPx = WIDTH_PX_SAFELIST[Math.min(Math.max(idx, 0), WIDTH_PX_SAFELIST.length - 1)];
  return `${widthPx}px`;
};


/**
 * 根据中文文本计算标签宽度
 * 在外语环境下返回固定宽度以简化计算
 * @param text 中文文本
 * @param isForeignLanguage 是否为外语环境
 * @returns 对应的宽度字符串
 */
export const getLabelWidth = (text: string, isForeignLanguage: boolean = false): string => {
  if (widthCache.has(text)) {
    return widthCache.get(text)!;
  }

  // 计算字符数：中文字数 + 外语字母数（两个字母算一个中文）
  const chineseChars = text.replace(/[a-zA-Z0-9\s]/g, '').length;
  const englishLetters = (text.match(/[a-zA-Z]/g) || []).length;
  const englishChars = Math.ceil(englishLetters / 2); // 两个字母算一个中文
  const totalChars = chineseChars + englishChars;

  const width = getSafelistWidthByCharCount(totalChars);

  widthCache.set(text, width);
  return width;
};

export const getSmartLabelClassName = (
  texts: string[]
): string => {
  const isForeignLanguage = isForeignLanguageEnv();
  const baseLabelClasses = 'text-gray-800 whitespace-nowrap !font-bold';
  const build = (width: string): string => {
    if (isForeignLanguage) {
      // 外语环境：标签在上（垂直布局）。
      // ⚠️ 不要强制固定宽度（例如 48px），否则长文案可能换行/高度不一致，导致同一行输入框上下错位。
      // 这里让 label 自适应容器宽度，并保持不换行。
      return `${baseLabelClasses} mb-1 text-left text-sm w-full min-w-0 flex-shrink-0`.trim();
    }

    // 中文环境：标签在左，水平布局，统一标签与输入框间距
    return `${baseLabelClasses} !mr-2 text-left leading-8 !w-[${width}] !min-w-0 !flex-shrink-0`.trim();
  };

  const sortedKey = JSON.stringify([...texts].sort());
  const cacheKey = `column-${sortedKey}-${isForeignLanguage}`;
  if (columnWidthCache.has(cacheKey)) {
    const maxWidth = columnWidthCache.get(cacheKey)!;
    return build(maxWidth);
  }

  // 计算该列中最宽的宽度
  const maxWidth = texts.length === 0
    ? getSafelistWidthByCharCount(2)
    : texts.reduce((max, text) => {
      const width = getLabelWidth(text, isForeignLanguage);
      return compareWidths(width, max) > 0 ? width : max;
    }, getLabelWidth(texts[0], isForeignLanguage));

  columnWidthCache.set(cacheKey, maxWidth);
  return build(maxWidth);
};

/**
 * 比较两个宽度字符串的大小
 * @param width1 宽度1 (如 "48px")
 * @param width2 宽度2 (如 "36px")
 * @returns 1 if width1 > width2, -1 if width1 < width2, 0 if equal
 */
const compareWidths = (width1: string, width2: string): number => {
  const num1 = parseInt(width1.replace('px', ''));
  const num2 = parseInt(width2.replace('px', ''));
  return num1 - num2;
};